import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import { useVisibilityObserver } from '@/common/hooks'
import type { ClaudeSession } from '@/features/events/conversation/state/types'
import type { TreeConfig } from '@/features/events/conversation/tree'
import { SessionTree } from '@/features/events/conversation/tree'
import { AgentModeSessionIcon } from '@/features/events/pages/events/components'
import { HostStatusIcon } from '@/features/status'
import {
  MOCK_SESSIONS,
  ANIMATED_SESSION_ID,
  DEMO_ACTIVITY_SCRIPT,
  ACKNOWLEDGED_BASH_ACTIVITY,
  FIX_RATE_LIMITER_ACTIVITY,
  SESSION_FIX_RATE_LIMITER,
} from '@/features/home/demo/data'
import { useDemoActivityPlayback } from '@/features/home/demo/hooks/useDemoActivityPlayback'
import { useLockedExpansion } from '@/features/home/demo/hooks/useLockedExpansion'
import { DemoSessionOrchestrator } from '@/features/home/demo'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const DemoSessionTree: FC = () => {
  const { ref, isVisible } = useVisibilityObserver(0.2)
  const { currentActivity, currentEvent } = useDemoActivityPlayback({
    script: DEMO_ACTIVITY_SCRIPT,
    isVisible,
  })
  const { isExpanded, onToggle } = useLockedExpansion({
    expandableKeys: EXPANDABLE_KEYS,
    initialExpansion: INITIAL_EXPANSION,
  })

  const externalExpansion = useMemo(() => ({ isExpanded, onToggle }), [isExpanded, onToggle])

  const renderSessionOrchestrator = useCallback(
    (session: ClaudeSession) => (
      <DemoSessionOrchestrator
        session={session}
        {...(session.sessionId === ANIMATED_SESSION_ID ? { currentActivity, currentEvent } : {})}
        {...(session.sessionId === SESSION_FIX_RATE_LIMITER.sessionId
          ? { userActivity: ACKNOWLEDGED_BASH_ACTIVITY, currentActivity: FIX_RATE_LIMITER_ACTIVITY }
          : {})}
      />
    ),
    [currentActivity, currentEvent],
  )

  return (
    <Box ref={ref} sx={containerSx}>
      <SessionTree
        sessions={MOCK_SESSIONS}
        config={DEMO_TREE_CONFIG}
        externalExpansion={externalExpansion}
        renderSessionOrchestrator={renderSessionOrchestrator}
      />
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EXPANDABLE_KEYS = new Set([
  'host:Macbook Pro',
  'project:/home/dev/projects/nexus-web',
  'project:/home/dev/projects/nexus-api',
  'project:/home/dev/projects/nexus-daemon',
  'session:00000000-0000-0000-0000-000000000001', // S1
  'session:00000000-0000-0000-0000-000000000002', // S2
  'session:00000000-0000-0000-0000-000000000003', // S3
  'session:00000000-0000-0000-0000-000000000004', // S4
  'session:00000000-0000-0000-0000-000000000005', // S5 (Quiet Hopper)
  'session:00000000-0000-0000-0000-000000000006', // S6 (Refactor Auth)
  'session:00000000-0000-0000-0000-000000000007', // S7 (Migrate DB)
  'session:00000000-0000-0000-0000-000000000008', // S8 (Optimize Encryption)
  'session:00000000-0000-0000-0000-000000000009', // S9 (Implement Key Rotation)
])

const INITIAL_EXPANSION: Record<string, boolean> = {
  'host:Macbook Pro': true,
  'project:/home/dev/projects/nexus-web': true,
  'project:/home/dev/projects/nexus-api': true,
  // nexus-daemon: deliberately omitted (defaults to false = collapsed)
  'session:00000000-0000-0000-0000-000000000001': true, // S1 Implement OAuth2
  'session:00000000-0000-0000-0000-000000000002': true, // S2 Add Form Validation
  'session:00000000-0000-0000-0000-000000000003': true, // S3 Fix Rate Limiter
  'session:00000000-0000-0000-0000-000000000004': true, // S4 Optimize Bundle Size
  'session:00000000-0000-0000-0000-000000000005': true, // S5 Quiet Hopper
  // S6-S9: deliberately omitted (defaults to false = collapsed)
}

const DEMO_TREE_CONFIG: TreeConfig = {
  icons: {
    // Demo: intentionally uses static HostStatusIcon without live status binding
    host: <HostStatusIcon fontSize="medium" />,
    project: <FolderOutlined />,
    session: (session: ClaudeSession) => <AgentModeSessionIcon session={session} />,
  },
  expandTogglePosition: 'left',
  persistExpansion: false,
  defaultExpanded: false,
  showCounts: false,
  sortOrder: 'newest',
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerSx: SxProps<Theme> = {
  position: 'relative',
  // Neutralize prod hover effects — demo tree items are non-interactive
  '& .MuiBox-root': {
    cursor: 'default !important',
  },
  '& .MuiBox-root:hover': {
    opacity: '1 !important',
  },
}
